import { computed, effect, inject, Injectable } from '@angular/core';
import { OOFProvidersEnum } from '../../../../../../enums/oofproviders.enum';
import { UpdateOfferProductDetailsResponse } from '../../../../../../models/order/api-models/order-pd-response.model';
import { CartCharges, CartSelectedCustomization } from '../../../../../../models/order/workflow/order-cart.model';
import { OrderStateServiceWrapper } from '../../../order.state.wrapper.service';
import { InternetProductResponseService } from '../../order-api-response/internet-pd-response.service';
import { OrderStateServiceV2 } from '../../order-state.service';

@Injectable({ providedIn: 'root' })
export class BrightspeedInternetCartStateService {
    private orderStateService = inject(OrderStateServiceV2);
    private internetPdResponseService = inject(InternetProductResponseService);
    private orderStateWrapper = inject(OrderStateServiceWrapper);
    private isBrightspeed = computed(() =>
        this.orderStateService.order()?.internetFlow?.provider?.toLowerCase() === OOFProvidersEnum.BRIGHTSPEED);
    private postRcc1Offers = computed(() => this.orderStateService.order()?.brightspeedPostRcc1Offers);

    readonly selectionContext = computed(() => {
        const internetFlow = this.orderStateService.order()?.internetFlow;
        return JSON.stringify({
            provider: internetFlow?.provider?.toLowerCase(),
            packageCode: internetFlow?.internetPackageSelection?.selectedPlanCode,
            offers: (internetFlow?.internetOffers?.offers ?? [])
                .map(offer => JSON.stringify([
                    offer.customizationCode ?? '', offer.choiceCode, offer.usoc ?? '',
                    String(offer.value ?? ''), offer.accepted ?? null
                ]))
                .sort()
        });
    });

    readonly postRcc1OffersApplied = computed(() => {
        const applied = this.postRcc1Offers();
        const order = this.orderStateService.order();
        return this.isBrightspeed() && !!applied
            && applied.orderNumber === order?.orderNumber
            && applied.selectionContext === this.selectionContext();
    });

    constructor() {
        effect(() => {
            if (this.postRcc1Offers() && !this.postRcc1OffersApplied()) {
                this.resetPostRcc1Offers();
            }
        });
    }

    resetPostRcc1Offers(): boolean {
        if (!this.postRcc1Offers()) {
            return false;
        }
        this.orderStateService.updateOrder({ brightspeedPostRcc1Offers: undefined });
        return true;
    }

    applyPostRcc1Offers(response: UpdateOfferProductDetailsResponse): boolean {
        const order = this.orderStateService.order();
        if (!this.isBrightspeed() || !order?.orderNumber) {
            return false;
        }

        const selectedOffers = order.internetFlow?.internetOffers?.offers ?? [];
        const offers: CartSelectedCustomization[] = [];
        for (const { customization } of this.internetPdResponseService.getUpdateOfferCustomizations(response)) {
            const choices = customization.Choices?.Choice?.filter(choice =>
                choice.CartVisible?.toLowerCase() === 'true'
                && choice.FlowVisible?.toLowerCase() === 'false'
                && choice.CartCategory === 'TodaysCharges') ?? [];
            if (!choices.length) {
                continue;
            }
            const applicableChoices = choices.length === 1 ? choices : choices.filter(choice =>
                selectedOffers.some(offer => offer.customizationCode === customization.CustomizationCode
                    && offer.choiceCode === choice.ChoiceCode));
            // Do not charge multiple alternatives without a confirmed applicable choice.
            if (applicableChoices.length !== 1) {
                return false;
            }
            offers.push({ Customization: customization, Choice: applicableChoices[0] });
        }

        this.orderStateWrapper.setInternetUpdateOffers(response);
        this.orderStateService.updateOrder({
            brightspeedPostRcc1Offers: {
                orderNumber: order.orderNumber,
                selectionContext: this.selectionContext(),
                offers
            }
        });
        return true;
    }

    getTodaysOffers(selectedCustomizations: CartSelectedCustomization[]): CartSelectedCustomization[] {
        if (!this.postRcc1OffersApplied()) {
            return selectedCustomizations;
        }

        const todaysOffers = [...selectedCustomizations];
        for (const offer of this.postRcc1Offers()?.offers ?? []) {
            if (!todaysOffers.some(selected => selected.Customization.CustomizationCode === offer.Customization.CustomizationCode
                && selected.Choice.ChoiceCode === offer.Choice.ChoiceCode)) {
                todaysOffers.push(offer);
            }
        }
        todaysOffers.sort((a, b) => (a.Choice?.CartSeq || 0) - (b.Choice?.CartSeq || 0));
        return todaysOffers;
    }

    isAdvancePaymentInCart(todayCharges: CartCharges): boolean {
        if (!this.isBrightspeed()) {
            return false;
        }

        const abpState = this.orderStateService.order()?.advanceBillPayment;
        return todayCharges.items.some(item =>
            item.customization?.CustomizationCode === abpState?.customization?.CustomizationCode
            && item.choiceCode === abpState?.customization?.Choices?.Choice?.ChoiceCode);
    }
}
